import React from 'react';
import { Box, Typography, CircularProgress, useMediaQuery, LinearProgress } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useDashboard } from '../../contexts/DashboardContext';
import { useDashboardData } from '../../hooks/useDashboardData';
import CongestionLegend from './CongestionLegend';
import { useColorPalette } from '../../contexts/ColorPaletteContext'; 
import AnalysisInfoButton from '../ui/AnalysisInfoButton'; // 追加
import WeatherIcon from '../ui/WeatherIcon'; // 追加
import { detectLanguageFromPathname } from '../../i18n';

// "YYYY-MM-DD" 形式の日付文字列をローカルタイムとして安全にパースする
// new Date("YYYY-MM-DD") は UTC midnight として解釈され、タイムゾーンによっては
// 1日ずれが生じるため、年/月/日を直接指定してローカルDateを生成する
const parseLocalDate = (dateStr) => {
    const parts = dateStr.split('-');
    return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
};

// 日付を整形する関数（例: "2024-07-01" -> "7/1"）
const formatDate = (dateStr) => {
    const date = parseLocalDate(dateStr);
    return `${date.getMonth() + 1}/${date.getDate()}`;
};

// 曜日を取得する関数（0: 日曜日 ~ 6: 土曜日）
const getDayOfWeek = (dateStr, isEnglish = false) => {
    const date = parseLocalDate(dateStr);
    const dayNames = isEnglish
        ? ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
        : ['日', '月', '火', '水', '木', '金', '土'];
    return dayNames[date.getDay()];
};

// 日付×時間ヒートマップコンポーネント
const DateTimeHeatmap = () => {
    const location = useLocation();
    const isEnglish = detectLanguageFromPathname(location.pathname) === 'en';
    const { 
        action: selectedAction, 
        year: selectedYear,
        month: selectedMonth,
        location: selectedLocation
    } = useDashboard();
    
    const { 
        calendarData, 
        loading,
        eventData,
    } = useDashboardData();
    
    // shouldShowCalculationNote関数を直接定義
    const shouldShowCalculationNote = () => {
        return selectedAction?.startsWith('wti') || selectedAction?.startsWith('dti');
    };
    const { getCellColor, getTextColor } = useColorPalette();
    // レスポンシブ対応のためのメディアクエリ
    const isMobile = useMediaQuery('(max-width:768px)');
    const isSmallMobile = useMediaQuery('(max-width:480px)');

    // クリックしたセルの参照とポップオーバーの状態管理
    // const [anchorEl, setAnchorEl] = useState(null);
    // const [popperText, setPopperText] = useState('');
    // const [open, setOpen] = useState(false);
    // const cellRefs = useRef({});

    // セルをタップした時の処理
    // const handleCellClick = (date, hour, highlightReason) => {
    //     const cellKey = `${date}-${hour}`;
    //     const cellElement = cellRefs.current[cellKey];
    //     
    //     if (anchorEl === cellElement) {
    //         // 同じセルをクリックした場合はポップオーバーを閉じる
    //         setOpen(false);
    //         setAnchorEl(null);
    //     } else {
    //         // 新しいセルをクリックした場合はポップオーバーを開く
    //         setAnchorEl(cellElement);
    //         setPopperText(highlightReason);
    //         setOpen(true);
    //     }
    // };

    // ポップオーバー以外の場所をクリックした時に閉じる
    // const handleClickAway = () => {
    //     setOpen(false);
    // };


    // このコンポーネントを表示すべきか判断
    const shouldShowComponent = selectedAction && selectedAction.startsWith('dti');
    
    // 表示される場合のみローディングを表示する条件
    const isLoading = shouldShowComponent && loading;

    // このコンポーネントが表示されない場合は何も返さない
    if (!shouldShowComponent) {
        return null;
    }

    // ローディング表示
    if (isLoading) {
        return (
            <Box sx={{ 
                maxWidth: '100%', 
                margin: '0 auto', 
                mt: 2, 
                px: isMobile ? 1 : 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography 
                        variant={isMobile ? "subtitle1" : "h6"} 
                        sx={{ textAlign: isMobile ? 'center' : 'left' }}
                    >
                        {isEnglish ? 'Date x Time congestion' : '日付×時間帯の混雑度'}
                    </Typography>
                </Box>
                
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    minHeight: '400px',
                    flexDirection: 'column',
                    gap: 2,
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                }}>
                    <CircularProgress 
                        size={48}
                        thickness={4}
                        sx={{ color: '#383947' }}
                    />
                    <Typography variant="h6" color="primary" fontWeight="bold">
                        {isEnglish ? 'Loading data...' : 'データを読み込み中...'}
                    </Typography>

                    <Box sx={{ width: '300px', mt: 1 }}>
                        <LinearProgress 
                            variant="indeterminate"
                            sx={{
                                height: 4,
                                borderRadius: 2,
                                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: 2,
                                    backgroundColor: '#383947',
                                }
                            }}
                        />
                    </Box>
                </Box>
            </Box>
        );
    }

    // calendarDataが空の場合は何も表示しない
    if (!calendarData || calendarData.length === 0) {
        return null;
    }

    // actionが"dti"で始まる場合のみ表示
    if (!selectedAction || !selectedAction.startsWith('dti')) {
        return null;
    }

    // データ形式チェックを行うが、明らかに一部のデータがあれば表示する
    let hasValidData = false;
    try {
        // 少なくとも1つのアイテムでdate属性とhours配列があれば有効とみなす
        for (const item of calendarData) {
            if (item && item.date && Array.isArray(item.hours) && item.hours.length > 0) {
                hasValidData = true;
                break;
            }
        }
    } catch (error) {
        console.error("Error checking data format:", error);
    }

    if (!hasValidData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                <Typography variant="body1" color="error">
                    {isEnglish ? 'Invalid data format. Please select another action.' : 'データ形式が正しくありません。別のアクションを選択してください。'}
                </Typography>
            </Box>
        );
    }

    // 表示は7時から19時まで（22時までは出さない）
    const hours = Array.from({ length: 13 }, (_, i) => i + 7); // 7時から19時まで

    // 日付でソート - エラーハンドリング付き
    let sortedData = [];
    try {
        sortedData = [...calendarData].sort((a, b) => {
            return new Date(a.date) - new Date(b.date);
        });
    } catch (error) {
        console.error("Error sorting data:", error);
        // エラーが発生しても、元のデータをそのまま使用
        sortedData = calendarData;
    }

    // 場所名の取得（ファイル名部分のみ）
    const getPlaceName = () => {
        if (!selectedLocation) return 'default';
        const parts = selectedLocation.split('/');
        const filename = parts[parts.length - 1];
        return filename.replace('.csv', '');
    };

    // 指定した日付のイベント情報を取得
    const getEventsForDate = (dateStr) => {
        if (!eventData || !Array.isArray(eventData)) return [];
        if (!selectedYear || !selectedMonth) return [];
        
        try {
            const str = String(dateStr ?? '');
            let targetDateStr = '';
            if (str.includes('-')) {
                targetDateStr = str;
            } else {
                const dateNum = parseInt(str, 10);
                if (isNaN(dateNum)) return [];
                targetDateStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(dateNum).padStart(2, '0')}`;
            }
            
            return eventData.filter(event => {
                const eventDate = event?.date || event?.event_date;
                return eventDate === targetDateStr;
            }) || [];
        } catch (error) {
            console.error('Error in getEventsForDate:', error, 'dateStr:', dateStr);
            return [];
        }
    };

    return (
        // <ClickAwayListener onClickAway={handleClickAway}>
            <Box sx={{ 
                maxWidth: '100%', 
                margin: '0 auto', 
                mt: 2, 
                px: isMobile ? 1 : 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography 
                        variant={isMobile ? "subtitle1" : "h6"} 
                        sx={{ textAlign: isMobile ? 'center' : 'left' }}
                    >
                        {isEnglish ? 'Date x Time congestion' : '日付×時間帯の混雑度'}
                    </Typography>
                    
                    {/* 分析情報ボタンを追加 */}
                    <AnalysisInfoButton 
                        analysisType="dateTime"
                        place={getPlaceName()}
                    />
                </Box>

                <CongestionLegend
                    showCalculationNote={shouldShowCalculationNote()}
                    legendType="heatmap"
                />
                
                {/* ヘッダーとセルを分離したコンテナ */}
                <Box sx={{ 
                    border: '1px solid #ddd', 
                    borderRadius: '8px', 
                    overflow: 'hidden',
                    width: '100%'
                }}>
                    {/* スクロール可能なコンテンツエリア */}
                    <Box sx={{ 
                        display: 'flex',
                        position: 'relative',
                    }}>
                        {/* 日付ラベル列 - 垂直方向に固定 */}
                        <Box sx={{ 
                            display: 'flex',
                            flexDirection: 'column',
                            width: isMobile ? '50px' : '60px', 
                            minWidth: isMobile ? '50px' : '60px',
                            position: 'sticky',
                            left: 0,
                            zIndex: 2,
                            boxShadow: '2px 0 4px rgba(0,0,0,0.05)'
                        }}>
                            {/* 左上角（日付ヘッダー） */}
                            <Box sx={{ 
                                borderRight: '1px solid #ddd',
                                borderBottom: '1px solid #ddd',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                padding: isMobile ? '5px 2px' : '10px 5px',
                                backgroundColor: '#f5f5f5',
                                height: isMobile ? (isSmallMobile ? '45px' : '50px') : '50px',
                            }}>
                                <Typography 
                                    variant={isSmallMobile ? "bodyS" : "bodyM"} 
                                    fontWeight="bold"
                                >
                                    {isEnglish ? 'Date' : '日付'}
                                </Typography>
                            </Box>

                                            {/* 日付ラベル */}
                            {sortedData.map((dateData, rowIndex) => {
                                const events = getEventsForDate(dateData.date);
                                const hasEvents = events.length > 0;
                                const baseHeight = isMobile ? (isSmallMobile ? 45 : 50) : 50;
                                const eventHeight = hasEvents ? (isSmallMobile ? 12 : 14) : 0;
                                const totalHeight = baseHeight + eventHeight;
                                
                                return (
                                    <Box 
                                        key={`date-label-${dateData.date}`}
                                        sx={{ 
                                            borderRight: '1px solid #ddd',
                                            borderBottom: rowIndex !== sortedData.length - 1 ? '1px solid #ddd' : 'none',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'flex-start',
                                            alignItems: 'center',
                                            padding: isMobile ? '2px 0' : '4px 0',
                                            backgroundColor: '#f9f9f9',
                                            height: `${totalHeight}px`,
                                        }}
                                    >
                                        <Box sx={{ 
                                            display: 'flex', 
                                            flexDirection: 'column', 
                                            alignItems: 'center',
                                            gap: '1px',
                                            position: 'relative',
                                            flex: 1,
                                            justifyContent: 'center',
                                            width: '100%'
                                        }}>
                                            {/* 天気情報（右上に配置） */}
                                            {dateData.weather_info && (
                                                <Box sx={{
                                                    position: 'absolute',
                                                    top: '1px',
                                                    right: '1px',
                                                    width: isSmallMobile ? '14px' : '16px',
                                                    height: isSmallMobile ? '14px' : '16px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                                }}>
                                                    <WeatherIcon 
                                                        weather={dateData.weather_info.weather}
                                                        size="small"
                                                        showTemp={false}
                                                    />
                                                </Box>
                                            )}
                                            
                                            <Typography 
                                                variant={isSmallMobile ? "bodyS" : "bodyM"} 
                                                fontWeight="bold"
                                                sx={{ fontSize: isSmallMobile ? '14px' : '16px' }}
                                            >
                                                {formatDate(dateData.date)}
                                            </Typography>
                                            <Typography 
                                                variant="caption" 
                                                sx={{ 
                                                    fontSize: isSmallMobile ? '0.5rem' : '0.6rem', 
                                                    opacity: 0.8,
                                                    lineHeight: 1
                                                }}
                                            >
                                                ({getDayOfWeek(dateData.date, isEnglish)})
                                            </Typography>
                                        </Box>
                                        
                                        {/* イベント情報エリア */}
                                        {hasEvents && (
                                            <Box sx={{
                                                width: '100%',
                                                minHeight: isSmallMobile ? '12px' : '14px',
                                                backgroundColor: 'rgba(255, 255, 255, 0.75)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                padding: '1px 2px',
                                                overflow: 'visible'
                                            }}>
                                                <Typography 
                                                    sx={{ 
                                                        fontSize: isSmallMobile ? '8px' : '9px',
                                                        color: '#333',
                                                        fontWeight: '600',
                                                        lineHeight: '1.1',
                                                        textAlign: 'center',
                                                        whiteSpace: 'normal',
                                                        wordBreak: 'break-word',
                                                        hyphens: 'auto',
                                                        maxWidth: '100%'
                                                    }}
                                                    title={events.map(e => e.title).join(', ')}
                                                >
                                                    {events.map((event, index) => (
                                                        <span key={index}>
                                                            {event.title}
                                                            {index < events.length - 1 && (
                                                                <br />
                                                            )}
                                                        </span>
                                                    ))}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                        
                        {/* セルとヘッダーのスクロール可能なエリア */}
                        <Box sx={{ 
                            overflowX: 'auto',
                            overflowY: 'hidden',
                            // maxHeight: isMobile ? 'calc(100vh - 280px)' : 'calc(100vh - 300px)',
                            WebkitOverflowScrolling: 'touch',
                            zIndex: 1,
                            '&::-webkit-scrollbar': {
                                height: '8px',
                                width: '8px'
                            },
                            '&::-webkit-scrollbar-track': {
                                backgroundColor: '#f1f1f1',
                                borderRadius: '4px'
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: '#c1c1c1',
                                borderRadius: '4px'
                            }
                        }}>
                            {/* 時間帯ヘッダー */}
                            <Box sx={{ 
                                display: 'flex',
                                backgroundColor: '#f5f5f5',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                            }}>
                                {hours.map(hour => (
                                    <Box 
                                        key={`hour-${hour}`} 
                                        sx={{ 
                                            minWidth: isMobile ? (isSmallMobile ? '30px' : '40px') : '40px',
                                            width: isMobile ? (isSmallMobile ? '30px' : '40px') : '40px',
                                            textAlign: 'center', 
                                            padding: isMobile ? '4px 2px' : '6px 2px',
                                            borderRight: hour !== hours[hours.length - 1] ? '1px solid #ddd' : 'none',
                                            borderBottom: '1px solid #ddd',
                                            flexShrink: 0,
                                            height: isMobile ? (isSmallMobile ? '45px' : '50px') : '50px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <Typography 
                                            variant={isSmallMobile ? "bodyS" : "bodyM"}
                                            fontWeight="bold"
                                        >
                                            {hour}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>

                            {/* 時間ごとのセル（行） */}
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                {sortedData.map((dateData, rowIndex) => {
                                    const events = getEventsForDate(dateData.date);
                                    const hasEvents = events.length > 0;
                                    const baseHeight = isMobile ? (isSmallMobile ? 45 : 50) : 50;
                                    const eventHeight = hasEvents ? (isSmallMobile ? 12 : 14) : 0;
                                    const totalHeight = baseHeight + eventHeight;
                                    
                                    return (
                                        <Box 
                                            key={`date-row-${dateData.date}`} 
                                            sx={{ 
                                                display: 'flex', 
                                                flexDirection: 'row'
                                            }}
                                        >
                                        {/* 時間ごとのセル */}
                                        {hours.map((hour, colIndex) => {
                                            const hourData = dateData.hours.find(h => h.hour === hour);
                                            const congestion = hourData ? hourData.congestion : 0;
                                            const count = hourData ? hourData.count : 0;
                                            const isPrediction = hourData?.source === 'prediction';

                                            return (
                                                <Box
                                                    key={`${dateData.date}-${hour}`}
                                                    sx={{
                                                        minWidth: isMobile ? (isSmallMobile ? '30px' : '40px') : '40px',
                                                        width: isMobile ? (isSmallMobile ? '30px' : '40px') : '40px',
                                                        height: `${totalHeight}px`,
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        backgroundColor: congestion === 0 ? '#e0e0e0' : getCellColor(congestion),
                                                        borderRight: colIndex !== hours.length - 1 ? '1px solid #ddd' : 'none',
                                                        borderBottom: rowIndex !== sortedData.length - 1 ? '1px solid #ddd' : 'none',
                                                        position: 'relative',
                                                        cursor: 'default',
                                                        flexShrink: 0,
                                                        ...(isPrediction && {
                                                            outline: '2px dashed rgba(85, 60, 154, 0.55)',
                                                            outlineOffset: '-3px',
                                                        }),
                                                    }}
                                                    title={(() => {
                                                        const sourceLabel = isPrediction
                                                            ? (isEnglish ? ' [Predicted]' : ' [予測]')
                                                            : '';
                                                        return isEnglish
                                                            ? `${formatDate(dateData.date)} ${hour}:00${sourceLabel} ${congestion === 0 ? '(No data)' : `(Count: ${count})`}`
                                                            : `${formatDate(dateData.date)} ${hour}時${sourceLabel} ${congestion === 0 ? '(データなし)' : `(人数: ${count})`}`;
                                                    })()}
                                                >
                                                    {/* メインコンテンツエリア */}
                                                    <Box sx={{ 
                                                        flex: 1,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: congestion === 0 ? '#666' : getTextColor(congestion),
                                                    }}>
                                                        <Typography 
                                                            sx={{ 
                                                                fontSize: isMobile ? (isSmallMobile ? '18px' : '20px') : '22px',
                                                                lineHeight: '1',
                                                                fontWeight: 'bold',
                                                                textAlign: 'center'
                                                            }}
                                                        >
                                                            {congestion === 0 ? '-' : congestion}
                                                        </Typography>
                                                    </Box>
                                                    
                                                    {/* 天気情報エリア（右上に配置） */}
                                                    {hourData && hourData.weather_info && (
                                                        <Box sx={{
                                                            position: 'absolute',
                                                            top: '1px',
                                                            right: '1px',
                                                            width: isSmallMobile ? '12px' : '14px',
                                                            height: isSmallMobile ? '12px' : '14px',
                                                            backgroundColor: 'rgba(255, 255, 255, 0.85)',
                                                            borderRadius: '2px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                                        }}>
                                                            <WeatherIcon 
                                                                weather={hourData.weather_info.weather}
                                                                size="small"
                                                                showTemp={false}
                                                            />
                                                        </Box>
                                                    )}
                                                    {/* 予測セルの右下に「予測」バッジ */}
                                                    {isPrediction && (
                                                        <Box sx={{
                                                            position: 'absolute',
                                                            bottom: '1px',
                                                            right: '1px',
                                                            backgroundColor: 'rgba(85, 60, 154, 0.92)',
                                                            color: '#fff',
                                                            borderRadius: '3px',
                                                            padding: '0 2px',
                                                            fontSize: isSmallMobile ? '7px' : '8px',
                                                            fontWeight: 'bold',
                                                            letterSpacing: '0.02em',
                                                            zIndex: 3,
                                                            lineHeight: 1.1,
                                                            pointerEvents: 'none',
                                                        }}>
                                                            {isEnglish ? 'Pred' : '予測'}
                                                        </Box>
                                                    )}
                                                </Box>
                                            );
                                                                                                })}
                                                    </Box>
                                                    );
                                                })}
                            </Box>
                        </Box>
                    </Box>
                </Box>
                
                {/* ポッパーとレジェンドは変更なし */}
                {/*
                <Popper 
                    open={open} 
                    anchorEl={anchorEl}
                    placement={isMobile ? "bottom" : "top"}
                    modifiers={[
                        {
                            name: 'offset',
                            options: {
                                offset: [0, isMobile ? 5 : 10],
                            },
                        },
                        {
                            name: 'preventOverflow',
                            enabled: true,
                            options: {
                                boundary: document.body,
                            },
                        },
                    ]}
                    sx={{ 
                        zIndex: 1500
                    }}
                >
                    <Paper 
                        elevation={3} 
                        sx={{ 
                            p: isMobile ? 1.5 : 2, 
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            border: '1px solid #ddd',
                            maxWidth: isMobile ? '180px' : '200px',
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                            fontSize: isMobile ? (isSmallMobile ? '12px' : '14px') : undefined
                        }}
                    >
                        <Typography variant={isMobile ? "bodyS" : "bodyM"} fontWeight="bold">
                            {popperText}
                        </Typography>
                    </Paper>
                </Popper>
                */}

            </Box>
        // </ClickAwayListener>
    );
};

export default DateTimeHeatmap;
